/**
 * Vitrine — Premium header (iter-51 — Experience-First refonte).
 *
 * Top-level navigation:
 *   Hôtel  ▾  ·  Beach Life  ▾  ·  Membership  ▾  ·  Contact  ·  [bag icon]  ·  RÉSERVER
 *
 *   Hôtel  →  Chambres  ·  Salles  ·  Restaurant  ·  Spa & Hammam  ·  Lounge
 *   Beach Life  →  Beach Club  ·  Loisirs  ·  Événements
 *   Membership  →  Sunset Card  ·  Beach Card  ·  Royal Card
 *
 * Desktop : hover-opening dropdown panels with discreet under-line accent.
 * Mobile  : hamburger drawer with click-to-expand accordions for the three
 *           grouped items, flat links for the singletons.
 */
import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Download } from "lucide-react";
import { useComingSoon, checkComingSoon } from "../../context/ComingSoonContext";

// Singleton link helper.
const link = (to, label) => ({ to, label });

const NAV_GROUPS = [
  {
    key: "hotel",
    label: "Hôtel",
    items: [
      link("/univers/hebergement", "Hébergement"),
      link("/univers/salles", "Salles"),
      link("/le-kaai", "Restaurant"),
      link("/univers/lounge", "Lounge"),
    ],
  },
  {
    key: "beach-life",
    label: "Beach Life",
    items: [
      link("/univers/beach-club", "Beach Club"),
      link("/univers/activites", "Loisirs"),
      link("/univers/evenementiel", "Événements"),
    ],
  },
];

const SINGLETON_LINKS = [link("/blog", "Blog"), link("/contact", "Contact")];

export default function VitrineNav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [mobileOpenGroup, setMobileOpenGroup] = useState(null);
  const [livret, setLivret] = useState(null); // { available, url, filename }
  const hoverTimer = useRef(null);
  const loc = useLocation();
  const comingSoon = useComingSoon();

  // Central click handler for nav Links that may target a "not yet released" experience.
  // We use it inside `onClick` so the Link still contributes accessibility
  // (proper anchor semantics + right-click support) — we simply preventDefault
  // and open the gate modal for blocked destinations.
  function handleNavClick(e, to) {
    const gate = checkComingSoon(to);
    if (gate.blocked) {
      e.preventDefault();
      comingSoon.open(gate.label);
    }
  }

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setHoveredGroup(null);
    setMobileOpenGroup(null);
  }, [loc.pathname, loc.hash]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  // Fetch the currently published livret PDF (public endpoint, cached in state).
  useEffect(() => {
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${backend}/api/livret/pdf`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.available) setLivret(d); })
      .catch(() => {});
  }, []);

  const onHeroPage = loc.pathname === "/" ||
    loc.pathname === "/univers/beach-club" ||
    loc.pathname === "/univers/activites" ||
    loc.pathname === "/univers/hebergement" ||
    loc.pathname === "/univers/lounge" ||
    loc.pathname === "/le-kaai" ||
    loc.pathname.startsWith("/hebergement/");
  const onHero = onHeroPage && !scrolled && !menuOpen;
  const textColor = onHero ? "text-white" : "text-[#0A0A0A]";
  const linkColor = onHero
    ? "text-white/85 hover:text-[#D4B256]"
    : "text-[#0A0A0A]/80 hover:text-[#B8922A]";

  function openGroup(key) {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setHoveredGroup(key);
  }
  function scheduleClose() {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setHoveredGroup(null), 150);
  }

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled || menuOpen || hoveredGroup
            ? "bg-white/95 backdrop-blur-md border-b border-[#0A0A0A]/8"
            : "bg-transparent"
        }`}
        data-testid="vitrine-nav"
      >
        <div className="max-w-[1800px] mx-auto px-6 md:px-10 lg:px-12 h-20 md:h-20 lg:h-24 flex items-center gap-6">
          {/* MOBILE hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className={`lg:hidden p-2 -ml-2 transition-colors ${textColor}`}
            aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
            data-testid="vitrine-hamburger"
          >
            {menuOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>

          {/* LOGO */}
          <Link
            to="/"
            className="absolute left-1/2 -translate-x-1/2 lg:static lg:translate-x-0 lg:left-auto flex items-center"
            data-testid="vitrine-logo"
            aria-label="Boulay Beach Resort — Accueil"
          >
            <img
              src={
                onHero
                  ? "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/movphq5f_AA-02.png"
                  : "https://customer-assets.emergentagent.com/job_reserve-bbr/artifacts/b26hlnm6_AA_Plan%20de%20travail%201.png"
              }
              alt="BBR — Boulay Beach Resort"
              className="h-[38px] md:h-11 lg:h-12 w-auto transition-all duration-500"
            />
          </Link>

          {/* DESKTOP nav */}
          <nav
            className="hidden lg:flex flex-1 items-center justify-end gap-5 xl:gap-8 pr-4 xl:pr-6"
            data-testid="vitrine-inline-nav"
          >
            {NAV_GROUPS.map((g) => {
              const open = hoveredGroup === g.key;
              return (
                <div
                  key={g.key}
                  className="relative"
                  onMouseEnter={() => openGroup(g.key)}
                  onMouseLeave={scheduleClose}
                  data-testid={`nav-group-${g.key}`}
                >
                  <button
                    type="button"
                    className={`relative flex items-center gap-1.5 text-[0.7rem] xl:text-[0.75rem] tracking-[0.22em] uppercase font-medium transition-colors ${linkColor}`}
                  >
                    {g.label}
                    <ChevronDown size={11} strokeWidth={2} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {/* Dropdown panel */}
                  <div
                    className={`absolute top-full left-1/2 -translate-x-1/2 mt-6 min-w-[220px] transition-all duration-200 ${
                      open
                        ? "opacity-100 translate-y-0 pointer-events-auto"
                        : "opacity-0 -translate-y-2 pointer-events-none"
                    }`}
                  >
                    <div className="bg-white border border-[#0A0A0A]/8 shadow-xl py-3">
                      {g.items.map((it) => (
                        <Link
                          key={it.to}
                          to={it.to}
                          onClick={(e) => handleNavClick(e, it.to)}
                          className="block px-6 py-2.5 text-[0.72rem] tracking-[0.2em] uppercase text-[#0A0A0A]/75 hover:text-[#B8922A] hover:bg-[#FAF7F2] transition-colors"
                          data-testid={`nav-sub-${g.key}-${it.label.toLowerCase().replace(/\W+/g, "-")}`}
                        >
                          {it.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
            {SINGLETON_LINKS.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                className={`relative text-[0.7rem] xl:text-[0.75rem] tracking-[0.22em] uppercase font-medium transition-colors ${linkColor}`}
                data-testid={`nav-${it.label.toLowerCase()}`}
              >
                {it.label}
              </Link>
            ))}
          </nav>

          {/* Spacer mobile */}
          <div className="flex-1 lg:hidden" />

          {/* RÉSERVER — desktop only (mobile uses floating FAB) */}
          <div className="hidden lg:flex items-center justify-end ml-auto gap-2.5">
            {loc.pathname === "/" ? (
              <a
                href="#experiences"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById("experiences");
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className={`inline-flex items-center px-4 md:px-5 lg:px-6 py-2 md:py-2.5 text-[0.62rem] md:text-[0.7rem] tracking-[0.3em] uppercase font-medium transition-all duration-300 border ${
                  onHero
                    ? "text-white border-white hover:bg-[#B8922A] hover:border-[#B8922A]"
                    : "text-white bg-[#B8922A] border-[#B8922A] hover:bg-[#D4AF37] hover:border-[#D4AF37]"
                }`}
                data-testid="vitrine-cta-reserver"
              >
                Réserver
              </a>
            ) : (
              <Link
                to="/#experiences"
                className={`inline-flex items-center px-4 md:px-5 lg:px-6 py-2 md:py-2.5 text-[0.62rem] md:text-[0.7rem] tracking-[0.3em] uppercase font-medium transition-all duration-300 border ${
                  onHero
                    ? "text-white border-white hover:bg-[#B8922A] hover:border-[#B8922A]"
                    : "text-white bg-[#B8922A] border-[#B8922A] hover:bg-[#D4AF37] hover:border-[#D4AF37]"
                }`}
                data-testid="vitrine-cta-reserver"
              >
                Réserver
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* MOBILE DRAWER */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-500 ease-out lg:hidden ${
          menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="vitrine-mobile-menu"
      >
        <div className="absolute inset-0 bg-white" onClick={() => setMenuOpen(false)} />
        <div className="relative h-full overflow-y-auto pt-24 pb-12 px-6">
          <nav className="max-w-md mx-auto">
            <ul className="space-y-1">
              {NAV_GROUPS.map((g) => {
                const open = mobileOpenGroup === g.key;
                return (
                  <li key={g.key} className="border-b border-[#0A0A0A]/8" data-testid={`mobile-group-${g.key}`}>
                    <button
                      type="button"
                      onClick={() => setMobileOpenGroup(open ? null : g.key)}
                      className="w-full flex items-center justify-between py-4 text-left text-[#0A0A0A] hover:text-[#B8922A] transition-colors"
                      data-testid={`mobile-group-${g.key}-toggle`}
                    >
                      <span className="font-serif text-2xl leading-tight">{g.label}</span>
                      <ChevronDown
                        size={20}
                        strokeWidth={1.5}
                        className={`transition-transform ${open ? "rotate-180" : ""}`}
                      />
                    </button>
                    <ul
                      className={`overflow-hidden transition-[max-height,opacity] duration-300 ${
                        open ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                      }`}
                    >
                      {g.items.map((it) => (
                        <li key={it.to}>
                          <Link
                            to={it.to}
                            onClick={(e) => handleNavClick(e, it.to)}
                            className="block py-2.5 pl-1 text-[0.85rem] tracking-[0.14em] uppercase text-[#0A0A0A]/75 hover:text-[#B8922A] transition-colors"
                            data-testid={`mobile-sub-${g.key}-${it.label.toLowerCase().replace(/\W+/g, "-")}`}
                          >
                            {it.label}
                          </Link>
                        </li>
                      ))}
                      <li className="pb-3" />
                    </ul>
                  </li>
                );
              })}
              {SINGLETON_LINKS.map((it) => (
                <li key={it.to} className="border-b border-[#0A0A0A]/8">
                  <Link
                    to={it.to}
                    className="block py-4 font-serif text-2xl text-[#0A0A0A] hover:text-[#B8922A] transition-colors"
                    data-testid={`mobile-link-${it.label.toLowerCase()}`}
                  >
                    {it.label}
                  </Link>
                </li>
              ))}
              {livret?.available && (
                <li className="border-b border-[#0A0A0A]/8">
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL || ""}${livret.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="py-4 font-serif text-[1.35rem] leading-tight text-[#0A0A0A] hover:text-[#B8922A] transition-colors flex items-center gap-3"
                    data-testid="mobile-link-livret"
                    onClick={() => setMenuOpen(false)}
                  >
                    <Download size={18} strokeWidth={1.5} />
                    Télécharger notre livret
                  </a>
                </li>
              )}
              <li className="pt-6">
                <Link
                  to="/#experiences"
                  className="block w-full text-center px-6 py-3.5 text-[0.7rem] tracking-[0.3em] uppercase font-medium bg-[#B8922A] text-white hover:bg-[#D4AF37] transition-colors"
                  data-testid="mobile-cta-reserver"
                >
                  Réserver
                </Link>
              </li>
            </ul>
            <div className="mt-10 text-center text-[0.55rem] tracking-[0.55em] uppercase text-[#0A0A0A]/45">
              Île Boulay · Abidjan
            </div>
          </nav>
        </div>
      </div>
    </>
  );
}
